'use client';

import { memo, useRef } from 'react';
import cn from '@/utils/classnames';
import { ThirdPartyButton, ThirdPartyButtonProps } from './third-party-button';

export interface ThirdPartyButtonConfig extends Omit<ThirdPartyButtonProps, 'variant'> {
  /** 按钮唯一标识 */
  id: string;
}

export interface ThirdPartyButtonGroupProps {
  /** 按钮配置列表 */
  buttons: ThirdPartyButtonConfig[];
  /** 额外的CSS类名 */
  className?: string;
}

/**
 * 第三方按钮组傻瓜组件
 * 根据按钮数量自动调整布局和样式
 */
const ThirdPartyButtonGroupComponent = ({
  buttons,
  className,
}: ThirdPartyButtonGroupProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const buttonCount = buttons.length;

  // 根据按钮数量确定布局样式
  const getLayoutClasses = () => {
    if (buttonCount === 1) {
      return 'w-full';
    } else if (buttonCount === 2) {
      return 'flex gap-[10px]';
    } else {
      // 三个以上按钮
      return 'flex gap-[25px] overflow-x-auto overflow-y-hidden scrollbar-hide';
    }
  };

  // 根据按钮数量确定按钮变体
  const getButtonVariant = (): 'text' | 'icon-only' => {
    return buttonCount > 2 ? 'icon-only' : 'text';
  };

  if (buttonCount === 0) {
    return null;
  }

  const variant = getButtonVariant();
  const layoutClasses = getLayoutClasses();

  return (
    <div
      ref={scrollRef}
      className={cn(
        'flex items-center',
        layoutClasses,
        // 隐藏滚动条
        '[&::-webkit-scrollbar]:hidden',
        className
      )}
      style={{
        // 确保滚动容器不会显示滚动条
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
    >
      {buttons.map((button, index) => (
        <ThirdPartyButton
          key={button.id}
          {...button}
          variant={variant}
          className={cn(
            // 两个按钮时，平分宽度
            buttonCount < 3 && 'flex-1',
            // 三个以上按钮时，第一个和最后一个添加边距
            buttonCount > 2 && index === 0 && 'ml-[10px]',
            buttonCount > 2 && index === buttonCount - 1 && 'mr-[10px]',
            // 确保按钮不会被压缩
            buttonCount > 2 && 'flex-shrink-0',
            button.className
          )}
        />
      ))}
    </div>
  );
};

export const ThirdPartyButtonGroup = memo(ThirdPartyButtonGroupComponent);
